import type { HierarcicalObjectReference } from "@novorender/data-js-api";
import pMap from "p-map";

import { isLeafNode } from "utils/objectData";
import type { SelectionSet } from "utils/selectionSet";

import type { ObjectVisibilityStats, ObjectVisibilityStatsRequest } from "./types";

/**
 * Scene visibility stats out of the object DB, by reading each file's subtree. Section
 * planes are left out ({@link ObjectVisibilityStats.excludedObjectsRespectSectionPlanes}):
 * placing one would mean testing every object's bounds.
 */

/** The reads the stats need off the object DB. */
export interface ObjectDbVisibilityReads {
    /** The scene's files are the children of `undefined`. */
    getChildren(
        parent: HierarcicalObjectReference | undefined,
        signal?: AbortSignal,
    ): AsyncIterableIterator<HierarcicalObjectReference>;
    /** A node's subtree, the node itself excluded. */
    getDescendantIds(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<Uint32Array>;
    /**
     * The top-level path (file) each of `ids` sits under.
     * @returns `undefined` when a row did not come back: a failed lookup of a real id looks like an unknown id.
     */
    rootPathsOf(ids: SelectionSet, signal?: AbortSignal): Promise<Set<string> | undefined>;
}

const SUBTREE_READ_CONCURRENCY = 6;

/** How many ids {@link hiddenFileCandidates} will read paths for. */
const PATH_NARROWING_LIMIT = 5_000;

const EMPTY_ID_ARRAY = new Uint32Array();

/** The two figures, before the caveat that goes out with them. */
type VisibilityFigures = Omit<ObjectVisibilityStats, "excludedObjectsRespectSectionPlanes">;

/** A file and its subtree, the file's own node excluded. */
interface FileSubtree {
    file: HierarcicalObjectReference;
    ids: Uint32Array;
}

/** An abort answers the empty figures rather than rejecting. */
export async function readObjectDbVisibilityStats(
    reads: ObjectDbVisibilityReads,
    request: ObjectVisibilityStatsRequest,
): Promise<ObjectVisibilityStats> {
    // Ignored ids come out of the named set here, and out of the files in `fileNodes`.
    const ignored = request.ignoredObjectIds?.sortedIds ?? EMPTY_ID_ARRAY;
    const figures =
        request.mode === "isolated"
            ? await isolatedFigures(reads, request, request.visibleIds.withRemoved(ignored))
            : await hiddenFigures(reads, request, request.hiddenIds.withRemoved(ignored));
    return {
        ...figures,
        // Nothing was left out when there was nothing to leave out. The flag describes the
        // count, so it stays behind when the count does.
        ...(figures.excludedObjectCount === undefined
            ? {}
            : { excludedObjectsRespectSectionPlanes: request.sectionPlanes.length === 0 }),
    };
}

/** Both figures off one read of every file's subtree, which is also the scene total. */
async function isolatedFigures(
    reads: ObjectDbVisibilityReads,
    request: ObjectVisibilityStatsRequest,
    visibleIds: SelectionSet,
): Promise<VisibilityFigures> {
    const { includeVisibleFiles, includeExcludedObjects = true, signal } = request;
    if (!includeVisibleFiles && !includeExcludedObjects) {
        return {};
    }
    const files = await fileNodes(reads, request, signal);
    if (!files) {
        return {};
    }
    if (!includeExcludedObjects && !visibleIds.size) {
        // Nothing is visible, so no subtree read can put a file back on screen.
        return { visibleFileCount: 0 };
    }
    const subtrees = await readFileSubtrees(reads, files, signal);
    if (!subtrees) {
        return {};
    }
    return {
        visibleFileCount: includeVisibleFiles
            ? subtrees.filter((subtree) => showsSomethingVisible(subtree, visibleIds)).length
            : undefined,
        excludedObjectCount: excludedObjectCount(subtrees, visibleIds, includeExcludedObjects),
    };
}

/** The count is the hidden set; only files it reaches ({@link hiddenFileCandidates}) have subtrees read. */
async function hiddenFigures(
    reads: ObjectDbVisibilityReads,
    request: ObjectVisibilityStatsRequest,
    hiddenIds: SelectionSet,
): Promise<VisibilityFigures> {
    const { includeVisibleFiles, signal } = request;
    const figures: VisibilityFigures = { excludedObjectCount: hiddenIds.size };
    if (!includeVisibleFiles) {
        return figures;
    }
    const files = await fileNodes(reads, request, signal);
    if (!files) {
        return figures;
    }
    if (!hiddenIds.size) {
        return { ...figures, visibleFileCount: files.length };
    }
    const candidates = await hiddenFileCandidates(reads, files, hiddenIds, signal);
    if (signal?.aborted) {
        // Otherwise no candidates would read as every file showing something.
        return figures;
    }
    const subtrees = await readFileSubtrees(reads, candidates, signal);
    if (!subtrees) {
        return figures;
    }
    const emptied = subtrees.filter((subtree) => isEmptiedBy(subtree, hiddenIds)).length;
    return { ...figures, visibleFileCount: files.length - emptied };
}

/** The scene total (files plus subtrees) minus the visible set; `undefined` if any subtree is unread. */
function excludedObjectCount(
    subtrees: readonly FileSubtree[],
    visibleIds: SelectionSet,
    asked: boolean,
): number | undefined {
    if (!asked || subtrees.some(isUnread)) {
        return undefined;
    }
    const sceneObjectCount = subtrees.reduce((count, subtree) => count + subtree.ids.length + 1, 0);
    // An id the tree does not account for would otherwise take the count below zero.
    return Math.max(sceneObjectCount - visibleIds.size, 0);
}

/** The scene's files less the ignored ones, `undefined` for an abandoned read. */
async function fileNodes(
    reads: ObjectDbVisibilityReads,
    { ignoredObjectIds }: ObjectVisibilityStatsRequest,
    signal?: AbortSignal,
): Promise<HierarcicalObjectReference[] | undefined> {
    // `db.search` rejects on abort rather than ending early.
    const files = await Array.fromAsync(reads.getChildren(undefined, signal)).catch((e) => {
        if (signal?.aborted) {
            return undefined;
        }
        throw e;
    });
    return !files || signal?.aborted ? undefined : files.filter((file) => !ignoredObjectIds?.has(file.id));
}

/** `undefined` for an abandoned read. */
async function readFileSubtrees(
    reads: ObjectDbVisibilityReads,
    files: readonly HierarcicalObjectReference[],
    signal?: AbortSignal,
): Promise<FileSubtree[] | undefined> {
    return pMap(
        files,
        async (file) => ({
            file,
            // `isLeafNode` rather than the node type: a container file is often stamped
            // `Leaf` and still owns a subtree (BM-10439).
            ids: isLeafNode(file) ? EMPTY_ID_ARRAY : await reads.getDescendantIds(file, signal),
        }),
        { concurrency: SUBTREE_READ_CONCURRENCY, signal },
    ).catch((e) => {
        // `pMap` rejects when its signal fires, where the interface answers an abort with
        // an empty value. Anything else is a real failure and still throws.
        if (signal?.aborted) {
            return undefined;
        }
        throw e;
    });
}

/**
 * The files a hidden set could have emptied, narrowed by path. Every file is a candidate past
 * {@link PATH_NARROWING_LIMIT}, where the metadata read costs more than it saves, and when the
 * read comes up short, where narrowing would drop a file the missing ids empty.
 */
async function hiddenFileCandidates(
    reads: ObjectDbVisibilityReads,
    files: readonly HierarcicalObjectReference[],
    hiddenIds: SelectionSet,
    signal?: AbortSignal,
): Promise<readonly HierarcicalObjectReference[]> {
    if (hiddenIds.size > PATH_NARROWING_LIMIT) {
        return files;
    }
    const hiddenRootPaths = await reads.rootPathsOf(hiddenIds, signal);
    if (!hiddenRootPaths) {
        return files;
    }
    return files.filter((file) => hiddenRootPaths.has(file.path));
}

/** An empty subtree for a non-leaf is a failed read. */
function isUnread({ file, ids }: FileSubtree): boolean {
    return !ids.length && !isLeafNode(file);
}

/**
 * The file's own node counts. An unread subtree counts as showing: one file too many reads
 * as an estimate, one too few as a change.
 */
function showsSomethingVisible(subtree: FileSubtree, visibleIds: SelectionSet): boolean {
    return isUnread(subtree) || visibleIds.has(subtree.file.id) || subtree.ids.some((id) => visibleIds.has(id));
}

/** An unread subtree is not emptied — see {@link showsSomethingVisible}. */
function isEmptiedBy(subtree: FileSubtree, hiddenIds: SelectionSet): boolean {
    if (isUnread(subtree)) {
        return false;
    }
    // A file with no subtree stands on its own node alone.
    const ids = subtree.ids.length ? subtree.ids : Uint32Array.of(subtree.file.id);
    return ids.every((id) => hiddenIds.has(id));
}
